import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const screenshotContext = require.context(
  "../../assets/portfolio_screenshots",
  true,
  /\.(png|jpe?g|webp|avif)$/i
);

const screenshotsByProject = screenshotContext.keys().reduce((accumulator, key) => {
  const normalizedPath = key.replace("./", "");
  const [projectId, fileName] = normalizedPath.split("/");

  if (!projectId || !fileName) {
    return accumulator;
  }

  if (!accumulator[projectId]) {
    accumulator[projectId] = [];
  }

  accumulator[projectId].push({
    src: screenshotContext(key),
    fileName,
  });

  return accumulator;
}, {});

Object.keys(screenshotsByProject).forEach((projectId) => {
  screenshotsByProject[projectId].sort((left, right) =>
    left.fileName.localeCompare(right.fileName, undefined, { numeric: true, sensitivity: "base" })
  );
});

function renderParagraphBlocks(text, keyPrefix) {
  const rawBlocks = String(text || "")
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  const sentenceBlocks = rawBlocks.flatMap((paragraph) => {
    const sentences = paragraph.match(/[^.!?]+[.!?]?/g) || [];
    return sentences.map((sentence) => sentence.trim()).filter(Boolean);
  });

  return sentenceBlocks.map((paragraph, index) => (
    <p key={`${keyPrefix}-${index}`} className="paragraphBlock">
      {paragraph}
    </p>
  ));
}

function renderCommitItems(project, t) {
  const commitItems = Array.isArray(project.commits) ? project.commits : [];

  if (commitItems.length === 0) {
    return <p>{t("projectAccordion.noCommits")}</p>;
  }

  return (
    <ul className="roadmapChecklist">
      {commitItems.map((commit, index) => {
        const label = `${commit.date}${commit.message ? ` - ${commit.message}` : ""}`;
        const key = commit.sha || `${project.id}-commit-${index}`;

        return (
          <li key={key}>
            <span className="roadmapCheck" aria-hidden="true">
              {"-"}
            </span>
            <span>
              {commit.url ? (
                <a className="projectProjectLink" href={commit.url} target="_blank" rel="noreferrer">
                  {label}
                </a>
              ) : (
                label
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default function ProjectWindowContent({ project, isMaximized = false }) {
  const { t } = useTranslation("aiPortfolio");
  const screenshots = useMemo(() => screenshotsByProject[project.id] || [], [project.id]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [openPanel, setOpenPanel] = useState(null);
  const normalizedStatus = useMemo(() => {
    const status = String(project.status || "").trim();
    if (status === "Paused" || status === "OnGoing" || status === "Ready" || status === "Online" || status === "Backlog") {
      return status;
    }
    return "Ready";
  }, [project.status]);
  const isBacklog = normalizedStatus === "Backlog";
  const hasSlider = screenshots.length > 1;
  const hasScreenshots = screenshots.length > 0;

  const accordionItems = useMemo(
    () => [
      {
        id: "purpose",
        title: t("projectAccordion.purpose"),
        content: <>{renderParagraphBlocks(project.pitch, `${project.id}-pitch`)}</>,
      },
      {
        id: "functionalities",
        title: t("projectAccordion.functionalities"),
        content: Array.isArray(project.functionalitiesItems) ? (
          <ul className="roadmapChecklist">
            {project.functionalitiesItems.map((feature, index) => (
              <li key={`${project.id}-feature-${index}`}>
                <span className="roadmapCheck" aria-hidden="true">
                  {"-"}
                </span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p>{project.functionalities || project.pitch}</p>
        ),
      },
      {
        id: "stacks",
        title: t("projectAccordion.stacks"),
        content: (
          <div className="projectInfoChips">
            {project.stack.map((tech) => (
              <span key={`${project.id}-${tech}`}>{tech}</span>
            ))}
          </div>
        ),
      },
      {
        id: "commits",
        title: t("projectAccordion.commits"),
        content: isBacklog ? <p>{t("projectAccordion.toDefine")}</p> : renderCommitItems(project, t),
      },
      {
        id: "architecture",
        title: t("projectAccordion.architectureDomains"),
        content: <p>{project.architectureDomains}</p>,
      },
      {
        id: "roadmap",
        title: t("projectAccordion.roadmap"),
        content: Array.isArray(project.roadmapTasks) ? (
          <ul className="roadmapChecklist">
            {project.roadmapTasks.map((task, index) => (
              <li key={`${project.id}-roadmap-${index}`} className={task?.done ? "done" : ""}>
                <span className="roadmapCheck" aria-hidden="true">
                  {task?.done ? "\u2611" : "\u2610"}
                </span>
                <span>{task?.label}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p>{project.roadmap}</p>
        ),
      },
    ],
    [project, t, isBacklog]
  );
  const accordionGridRows = useMemo(
    () =>
      accordionItems
        .map((item) => (openPanel === item.id ? "minmax(0, 1fr)" : "auto"))
        .join(" "),
    [accordionItems, openPanel]
  );

  useEffect(() => {
    setActiveIndex(0);
    setOpenPanel(null);
  }, [project.id, screenshots.length]);

  const showPrevious = () => {
    setActiveIndex((current) => (current - 1 + screenshots.length) % screenshots.length);
  };

  const showNext = () => {
    setActiveIndex((current) => (current + 1) % screenshots.length);
  };

  if (!isMaximized) {
    return (
      <>
        <div className={`mockThumb ${project.mockTheme} ${hasScreenshots ? "hasScreenshot" : ""}`}>
          <p className={`mockThumbStatus status${normalizedStatus}`}>{normalizedStatus}</p>
          {hasScreenshots ? (
            <>
              <div className="mockThumbViewport">
                <div
                  className="mockThumbTrack"
                  style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                  aria-live="polite"
                >
                  {screenshots.map((screenshot, index) => (
                    <div key={screenshot.fileName} className="mockThumbSlide">
                      <img
                        className="mockThumbImage"
                        src={screenshot.src}
                        alt={t("project.screenshotAlt", { index: index + 1, project: project.name })}
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {hasSlider && (
                <>
                  <button
                    type="button"
                    className="mockThumbNav mockThumbNavPrev"
                    onClick={showPrevious}
                    aria-label={t("project.previousImage", { project: project.name })}
                  >
                    {"<"}
                  </button>
                  <button
                    type="button"
                    className="mockThumbNav mockThumbNavNext"
                    onClick={showNext}
                    aria-label={t("project.nextImage", { project: project.name })}
                  >
                    {">"}
                  </button>
                  <p className="mockThumbCounter" aria-live="polite">
                    {activeIndex + 1}/{screenshots.length}
                  </p>
                </>
              )}
            </>
          ) : (
            project.icon
          )}
        </div>
        <div className="projectCompactInfo">
          <p className="projectCompactPitch">{project.pitchCompact || project.pitch}</p>
          <p className="projectCompactMeta">
            <strong>{t("project.category")}:</strong> {project.category}
          </p>
          <p className="projectCompactDates">
            {isBacklog ? (
              <>
                <strong>{t("projectAccordion.start")}:</strong> {t("projectAccordion.toDefine")}
              </>
            ) : (
              <>
                <strong>{t("projectAccordion.start")}:</strong> {project.commitStart} |{" "}
                <strong>{t("projectAccordion.end")}:</strong> {project.commitEnd}
              </>
            )}
          </p>
          <p className="projectCompactLinkRow">
            <strong>{t("project.linkLabel")}:</strong>{" "}
            <a className="projectProjectLink" href={project.projectLink} target="_blank" rel="noreferrer">
              {project.projectLink}
            </a>
          </p>
        </div>
      </>
    );
  }

  return (
    <div className="projectContentLayout">
      <section className="projectVisualColumn">
        <div className={`mockThumb ${project.mockTheme} ${hasScreenshots ? "hasScreenshot" : ""}`}>
          <p className={`mockThumbStatus status${normalizedStatus}`}>{normalizedStatus}</p>
          {hasScreenshots ? (
            <>
              <div className="mockThumbViewport">
                <div
                  className="mockThumbTrack"
                  style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                  aria-live="polite"
                >
                  {screenshots.map((screenshot, index) => (
                    <div key={screenshot.fileName} className="mockThumbSlide">
                      <img
                        className="mockThumbImage"
                        src={screenshot.src}
                        alt={t("project.screenshotAlt", { index: index + 1, project: project.name })}
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {hasSlider && (
                <>
                  <button
                    type="button"
                    className="mockThumbNav mockThumbNavPrev"
                    onClick={showPrevious}
                    aria-label={t("project.previousImage", { project: project.name })}
                  >
                    {"<"}
                  </button>
                  <button
                    type="button"
                    className="mockThumbNav mockThumbNavNext"
                    onClick={showNext}
                    aria-label={t("project.nextImage", { project: project.name })}
                  >
                    {">"}
                  </button>
                  <p className="mockThumbCounter" aria-live="polite">
                    {activeIndex + 1}/{screenshots.length}
                  </p>
                </>
              )}
            </>
          ) : (
            project.icon
          )}
        </div>
      </section>

      <aside className="projectInfoMenu" aria-label={t("projectAccordion.ariaLabel")}>
        <div className="projectAccordionList" style={{ gridTemplateRows: accordionGridRows }}>
          {accordionItems.map((item) => {
            const expanded = openPanel === item.id;
            return (
              <div key={item.id} className={`projectAccordionItem ${expanded ? "open" : ""}`}>
                <button
                  type="button"
                  className="projectAccordionTrigger"
                  aria-expanded={expanded}
                  onClick={() => setOpenPanel((current) => (current === item.id ? null : item.id))}
                >
                  {item.title}
                </button>
                {expanded && <div className="projectAccordionPanel">{item.content}</div>}
              </div>
            );
          })}
        </div>
        <div className="projectInfoMenuBottom">
          <strong>{t("project.linkLabel")}:</strong>{" "}
          <a className="projectProjectLink" href={project.projectLink} target="_blank" rel="noreferrer">
            {project.projectLink}
          </a>
        </div>
      </aside>
    </div>
  );
}


